import { ClientKind, useClientKind } from '@/features/tr-reportsettings/clientKind';
import { selectedClient } from '@/core/selectedClient';

const COMPANY_PAGE = 'https://find-and-update.company-information.service.gov.uk/company/';

/**
 * APT359: the public Companies House page of a company, from the registration number saved on the client. Companies House numbers are 8
 * characters: a number short of that is padded with zeros (1234567 -> 01234567), after its letters when it has any (SC12345 -> SC012345).
 * Null when there is no usable number, so the screen offers no link rather than a wrong one.
 */
export function companiesHouseCompanyUrl(registrationNumber: string | null | undefined): string | null {
  const number = (registrationNumber ?? '').replace(/\s+/g, '').toUpperCase();
  const match = /^([A-Z]{0,2})(\d+)$/.exec(number);
  if (!match || number.length > 8) {
    return null;
  }
  const [, prefix, digits] = match;
  return `${COMPANY_PAGE}${prefix}${digits.padStart(8 - prefix.length, '0')}`;
}

/** APT363: the item sits first in the Integration side menu, named as the client asked. */
export const COMPANIES_HOUSE_MENU_LABEL = 'View on Companies House';

export interface CompaniesHouseMenuLink {
  /** True for a sole trader or partnership: no registration number, no Companies House page, so the menu offers nothing. */
  hidden: boolean;
  /** The public company page, or null when there is no usable registration number (the item is then shown disabled). */
  url: string | null;
  /** Why the item is disabled, for its tooltip. */
  reason: string;
}

/**
 * APT359 / APT363: what the "View on Companies House" menu item shows for the selected client: a plain link (a new tab) to the company's
 * public page, so its public information is one click away without searching for the company again. Nothing is sent to Companies House
 * from here. A sole trader or partnership has no registration number and no Companies House page, so nothing is shown for them.
 */
export function useCompaniesHouseMenuLink(): CompaniesHouseMenuLink {
  const client = selectedClient();
  const kind = useClientKind(client?.id);

  if (kind && (kind.clientType === ClientKind.SoleTrader || kind.clientType === ClientKind.Partnership)) {
    return { hidden: true, url: null, reason: '' };
  }
  const url = kind ? companiesHouseCompanyUrl(kind.registrationNumber) : null;
  const reason = !client
    ? 'Select a client first'
    : kind
      ? 'This client has no Companies House registration number'
      : 'Reading the client';
  return { hidden: false, url, reason };
}
